import React, { FC, useMemo, useState } from "react";
import classNames from "classnames/dedupe";
import { mergeDeepRight } from "ramda";
import { Carousel, ICarousel } from "../../ActionsSelectors/Carousel";
import { Divider } from "../Divider";
import { Button } from "../../ActionsSelectors/Button";
import { SvgIcon } from "../../Icons";
import HeroBannerThumbSlide from "./HeroBannerThumbSlide";
import HeroBannerSlide from "./HeroBannerSlide";
import { IHeroBannerProps } from "./HeroBanner.interface";

const getCommonCarouselParams = (delay: number) => ({
  autoplay: { delay },
  // removeClippedSubviews: false,
  slidesPerView: `auto`,
  centeredSlides: false,
  initialSlide: 0,
});

const MultiBanner = ({ delay, banners, slideType }) => {
  const [primaryInstance, setPrimaryInstance] =
    useState<ICarousel["params"]["mainCarouselInstance"]>(null);
  const [secondaryInstance, setSecondaryInstance] =
    useState<ICarousel["params"]["secondaryCarouselInstance"]>(null);

  const carouselParams: ICarousel["params"] = useMemo(() => {
    let params = {
      mainCarouselInstance: primaryInstance,
      secondaryCarouselInstance: secondaryInstance,
    };
    if (delay) {
      params = mergeDeepRight(params, {
        mainCarouselParams: { ...getCommonCarouselParams(delay) },
        secondaryCarouselParams: { ...getCommonCarouselParams(delay) },
      });
    }
    return params;
  }, [delay, primaryInstance, secondaryInstance]);

  return (
    <>
      <Carousel
        variant="hero-banner-carousel"
        params={carouselParams}
        onInstance={setPrimaryInstance}
      >
        {banners.map((banner) => (
          <HeroBannerSlide
            key={banner.id}
            slideType={slideType}
            slideData={banner}
          />
        ))}
      </Carousel>

      <div className="uds-carousel-hero__controls my-7 my-lg-6">
        <ul className="swiper-pagination nav-list flex-grow-1">
          <Carousel
            variant="hero-banner-carousel-thumbs"
            params={carouselParams}
            onInstance={setSecondaryInstance}
          >
            {banners.map((banner) => (
              <HeroBannerThumbSlide key={banner.id} banner={banner} />
            ))}
          </Carousel>
        </ul>
        <div className="d-none d-lg-flex hstack gap-6 p-2 ms-7">
          <Button
            variant="icon"
            classes={{
              root: "uds-carousel-hero__prev",
            }}
          >
            <SvgIcon
              classes={{ root: "icon-container uds-carousel-hero__icon" }}
              type="svg-icon"
              icon="NavArrowBackIcon"
            />
          </Button>
          <Button
            variant="icon"
            classes={{
              root: "uds-carousel-hero__next",
            }}
          >
            <SvgIcon
              classes={{ root: "icon-container uds-carousel-hero__icon" }}
              type="svg-icon"
              icon="NavArrowNextIcon"
            />
          </Button>
        </div>
      </div>
    </>
  );
};

const HeroBanner: FC<IHeroBannerProps> = ({
  banners,
  classes,
  slideType = "PromoWidget",
  delay,
}) => {
  const rootClasses = classNames(
    "hero-banner position-relative",
    classes?.root,
  );

  return (
    <div className={rootClasses} data-testid="HeroBanner">
      {banners.length > 1 ? (
        <MultiBanner delay={delay} banners={banners} slideType={slideType} />
      ) : (
        <HeroBannerSlide slideType={slideType} slideData={banners[0]} />
      )}

      <Divider
        color="grey"
        classes={{
          root: classNames({
            "mt-6": banners.length <= 1,
          }),
        }}
      />
    </div>
  );
};

export default HeroBanner;
